import * as React from "react";

export interface IOberinStoreConfiguration {
    [key: string]: {
        initialValue: any;
        persist?: boolean;
    };
}

export interface IOberinStoreContextProviderProps {
    configuration: IOberinStoreConfiguration;
    children: React.ReactNode;
}

interface IOberinStoreGlobalState {
    [key: string]: any;
}

interface IOberinStoreListener {
    id: string;
    setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IOberinStoreContext {
    config: IOberinStoreConfiguration;
    globalState: React.MutableRefObject<IOberinStoreGlobalState>;
    listeners: React.MutableRefObject<IOberinStoreListener[]>;
    cleanup: (
        identifier: string,
        setTrigger: React.Dispatch<React.SetStateAction<boolean>>,
    ) => void;
}

const OberinStoreContext = React.createContext<IOberinStoreContext | undefined>(
    undefined,
);

const getConfigurationPersist = (
    configuration: IOberinStoreConfiguration,
    key: string,
): boolean => {
    return configuration[key] && configuration[key].persist ? true : false;
};

const getConfigurationValue = (
    configuration: IOberinStoreConfiguration,
    key: string,
) => {
    let value = undefined;

    if (!configuration[key]) {
        return value;
    }

    if (configuration[key].initialValue === undefined) {
        value = null;
    } else if (
        typeof configuration[key].initialValue === "string" ||
        typeof configuration[key].initialValue === "number" ||
        typeof configuration[key].initialValue === "boolean"
    ) {
        value = configuration[key].initialValue;
    } else if (Array.isArray(configuration[key].initialValue)) {
        value = [...configuration[key].initialValue];
    } else if (Object.keys(configuration[key].initialValue).length > 0) {
        value = { ...configuration[key].initialValue };
    } else {
        value = configuration[key].initialValue;
    }

    return value;
};

const setInitialState = (
    configuration: IOberinStoreConfiguration,
): IOberinStoreGlobalState => {
    let state: IOberinStoreGlobalState = {};

    Object.keys(configuration).forEach((key: string) => {
        const value = getConfigurationValue(configuration, key);
        state[key] = value;
    });

    return state;
};

export const OberinStoreContextProvider: React.FunctionComponent<
    IOberinStoreContextProviderProps
> = ({ configuration, children }) => {
    const [config] = React.useState<IOberinStoreConfiguration>(configuration);

    const globalState = React.useRef<IOberinStoreGlobalState>(
        setInitialState(configuration),
    );
    const listeners = React.useRef<IOberinStoreListener[]>([]);

    const cleanup = React.useCallback(
        (
            identifier: string,
            setTrigger: React.Dispatch<React.SetStateAction<boolean>>,
        ) => {
            listeners.current = listeners.current.filter(
                (listener) => listener.setTrigger !== setTrigger,
            );
            if (
                listeners.current.findIndex(
                    (listener) => listener.id === identifier,
                ) === -1
            ) {
                if (!getConfigurationPersist(config, identifier)) {
                    globalState.current[identifier] = getConfigurationValue(
                        config,
                        identifier,
                    );
                }
            }
        },
        [config],
    );

    return (
        <OberinStoreContext.Provider
            value={{ config, globalState, listeners, cleanup }}
        >
            {children}
        </OberinStoreContext.Provider>
    );
};

export const useOberinStore = <T,>(
    identifier: string,
): [T, (getUpdatedState: T | ((previousState: T) => T)) => void] => {
    const [, setTrigger] = React.useState<boolean>(false);
    const context = React.useContext(OberinStoreContext);
    if (!context) {
        throw new Error(
            "No OberinStoreContextProvider found when calling useOberinStore<T>.",
        );
    }

    const { config, globalState, listeners, cleanup } = context;

    const dispatch = React.useCallback(
        (getUpdatedState: T | ((previousState: T) => T)) => {
            let updatedState: T;

            if (typeof getUpdatedState === "function") {
                let currentState = globalState.current[identifier] as T;
                let callback = getUpdatedState as (previousState: T) => T;
                updatedState = callback(currentState);
            } else {
                updatedState = getUpdatedState;
            }

            if (
                updatedState === null ||
                updatedState === undefined ||
                typeof updatedState === "string"
            ) {
                globalState.current[identifier] = updatedState;
            } else if (Array.isArray(updatedState)) {
                globalState.current[identifier] = [...updatedState];
            } else if (Object.keys(updatedState).length === 0) {
                globalState.current[identifier] = updatedState;
            } else if (
                !globalState.current[identifier] ||
                (globalState.current[identifier] &&
                    Object.keys(globalState.current[identifier]).length === 0)
            ) {
                globalState.current[identifier] = { ...updatedState };
            } else {
                globalState.current[identifier] = {
                    ...globalState.current[identifier],
                    ...updatedState,
                };
            }

            listeners.current
                .filter((li) => li.id === identifier)
                .forEach((li) => {
                    li.setTrigger((curr) => !curr);
                });
        },
        [identifier, globalState, listeners],
    );

    React.useEffect(() => {
        listeners.current.push({
            id: identifier,
            setTrigger: setTrigger,
        });

        return () => {
            cleanup(identifier, setTrigger);
        };
    }, [identifier, listeners, config, globalState, cleanup]);

    const state = globalState.current[identifier] as T;
    return [state, dispatch];
};
